import type React from "react";
import clsx from "clsx";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import Icon from "../../common/Icon/Icon";

import { createPluggable } from "pluggable-react";
import type { InputProps } from "../../../types/FormTypes";
import styles from "./Checkbox.module.scss";
import { withRefProp } from "../../../utils/withRefProp";

export interface CheckboxProps extends Omit<React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, "onCheckedChange" | "checked" | "value" | "onBlur" | "onFocus">, Omit<InputProps<boolean>, "onChange"> {
  ref?: React.Ref<HTMLButtonElement>;
  size?: "sm" | "md";
  /** When true, show indeterminate state (e.g. some but not all children selected). */
  indeterminate?: boolean;
  checked?: boolean | "indeterminate";
  onCheckedChange?: (checked: boolean) => void;
}

const DefaultCheckbox = withRefProp((props: CheckboxProps) => {
  const { className, value, onValueChange, disabled, indeterminate, checked, onCheckedChange, size = "md", ref, ...otherProps } = props;

  const normalizeToBool = (input: unknown): boolean => {
    if (input === true) return true;
    if (input === false || input == null) return false;
    if (typeof input === "string") {
      const normalized = input.trim().toLowerCase();
      if (normalized === "true" || normalized === "on") return true;
      if (normalized === "false" || normalized === "off" || normalized === "") return false;
    }
    return Boolean(input);
  };

  const handleCheckedChange = (newChecked: boolean | "indeterminate") => {
    const next = normalizeToBool(newChecked);
    onCheckedChange?.(next);
    onValueChange?.(next);
  };

  const resolvedChecked = indeterminate ? "indeterminate" : normalizeToBool(checked ?? value);

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      className={clsx(styles.checkbox, className)}
      checked={resolvedChecked}
      onCheckedChange={handleCheckedChange}
      disabled={disabled}
      data-size={size}
      data-deci-checkbox="true"
      {...otherProps}
    >
      <CheckboxPrimitive.Indicator className={styles.indicator}>
        {resolvedChecked === "indeterminate" ? (
          <Icon name="minus" className={styles.checkIcon} />
        ) : (
          <Icon name="check" className={styles.checkIcon} />
        )}
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});

const Checkbox = createPluggable<CheckboxProps>("deci.checkbox", DefaultCheckbox);

interface FavoriteProps extends Omit<React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, "children" | "checked" | "onCheckedChange"> {
  iconClassName?: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

export const Favorite = ({ className, iconClassName, onCheckedChange, ...props }: FavoriteProps) => (
  <CheckboxPrimitive.Root
    className={clsx(styles.favorite, className)}
    data-deci-favorite="true"
    onCheckedChange={onCheckedChange ? (v) => onCheckedChange(v === true) : undefined}
    {...props}
  >
    <Icon
      name="star"
      className={clsx(styles.favoriteIcon, iconClassName)}
      data-state={props.checked ? "checked" : "unchecked"}
    />
  </CheckboxPrimitive.Root>
);

interface LockToggleProps extends Omit<React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>, "children" | "checked" | "onCheckedChange"> {
  iconClassName?: string;
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

export const LockToggle = ({ className, iconClassName, checked, onCheckedChange, ...props }: LockToggleProps) => (
  <CheckboxPrimitive.Root
    className={clsx(styles.lockToggle, className)}
    data-deci-lock-toggle="true"
    checked={checked}
    onCheckedChange={onCheckedChange ? (v) => onCheckedChange(v === true) : undefined}
    {...props}
  >
    {checked ? (
      <Icon name="lock" className={clsx(styles.lockIcon, iconClassName)} />
    ) : (
      <Icon name="unlock" className={clsx(styles.lockIcon, iconClassName)} />
    )}
  </CheckboxPrimitive.Root>
);

export default Checkbox;
